import React, { useState, useEffect, useMemo, useRef } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Table,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import excelIcon from "../../../src/assets/images/excel.svg"

import axios from "axios"
import apiURl from "url"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import * as XLSX from "xlsx" // Add this: npm install xlsx
import { filter } from "lodash"

const RatesForCarrier = () => {
  const { carrierId } = useParams()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [rates, setRates] = useState([])
  const [uniqueZoneValues, setUniqueZoneValues] = useState([]) // For dropdown: unique zone values from zones
  const [isLoading, setIsLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedRateId, setSelectedRateId] = useState(null)
  const [uploadData, setUploadData] = useState([]) // Parsed Excel data for preview
  const [uploadErrors, setUploadErrors] = useState([]) // Errors/duplicates for preview
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [addFormData, setAddFormData] = useState({
    rate_type: "document",
    weight: "",
    zone_value: "",
    rate: "",
  })
  const [editFormData, setEditFormData] = useState({
    rate_type: "document",
    weight: "",
    zone_value: "",
    rate: "",
  })
  const [editingRateId, setEditingRateId] = useState(null)
  const [duplicates, setDuplicates] = useState([]) // Add this state
  //Carriers
  const [carrierName, setCarrierName] = useState("")
  const fetchCarriers = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/list`)
      if (data.status === "success") {
        const matchedCarrier = data.carriers.filter(
          carrier => carrier.id === Number(carrierId)
        )[0]
        if (matchedCarrier) {
          setCarrierName(matchedCarrier.name)
        } else {
          console.log("No carrier found with ID:", carrierId)
        }
      } else {
        toastr.error(data.message || "Failed to fetch carriers")
      }
    } catch (err) {
      toastr.error("Error fetching carriers")
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch unique zone values for this carrier (for dropdown)
  const fetchZoneValues = async () => {
    try {
      const { data } = await axios.get(`${apiURl}/carriers/${carrierId}/zones`)
      if (data.status === "success") {
        const uniqueValues = [...new Set(data.data.map(z => z.value))].sort(
          (a, b) => a - b
        )
        setUniqueZoneValues(uniqueValues)
      }
    } catch (err) {
      console.error("Error fetching zone values:", err)
    }
  }

  useEffect(() => {
    fetchCarriers()
    fetchZoneValues()
  }, [carrierId])

  const downloadSampleExcel = () => {
    const sampleData = [
      ["Rate Type", "Weight (Kg)", "Zone Value", "Rate"],
      ["document", 0.5, 1, 7588.8823],
      ["document", 1.0, 1, 8745.5923],
      ["non_document", 0.5, 1, 9329.3524],
      ["non_document", 1.0, 1, 10594.1661],
    ]

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(sampleData)
    XLSX.utils.book_append_sheet(wb, ws, "Rates")
    XLSX.writeFile(wb, `sample-rates-${carrierId}.xlsx`)
  }

  //Original Data
  const handleDownload = () => {
    const data = [
      ["Rate Type", "Weight (Kg)", "Zone Value", "Rate"],
      ...rates.map(rate => [
        rate.rate_type,
        rate.weight,
        rate.zone_value,
        rate.rate,
      ]),
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Rates")
    XLSX.writeFile(wb, `${carrierName}_rates_export.xlsx`)
  }

  // Fetch rates for this carrier
  const fetchRates = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/${carrierId}/rates`)
      if (data.status === "success") {
        setRates(data.data)
      } else {
        setError(data.message || "Failed to fetch rates")
      }
    } catch (err) {
      console.error("Error fetching rates:", err)
      setError("Error fetching rates")
    } finally {
      setIsLoading(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRates()
  }, [carrierId])

  const columns = useMemo(
    () => [
      {
        Header: "Rate Type",
        accessor: "rate_type",
        Cell: ({ value }) => (
          <Badge color={value === "document" ? "primary" : "secondary"}>
            {value}
          </Badge>
        ),
      },
      {
        Header: "Weight (Kg)",
        accessor: "weight",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Zone Value",
        accessor: "zone_value",
        Cell: ({ value }) => <Badge color="info">{value}</Badge>,
      },
      {
        Header: "Rate",
        accessor: "rate",
        Cell: ({ value }) => <span>{parseFloat(value).toFixed(4)}</span>,
      },
      {
        Header: "Action",
        accessor: "id",
        disableFilters: true,
        Cell: ({ row, value }) => (
          <div className="d-flex gap-2">
            <Button
              color="warning"
              size="sm"
              onClick={() => openEdit(row.original)}
            >
              Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setSelectedRateId(row.original.id)
                setShowDeleteModal(true)
              }}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  const handleDelete = () => {
    if (selectedRateId) {
      removeRate(selectedRateId)
    }
    setShowDeleteModal(false)
  }

  // Handle add form input changes
  const handleAddInputChange = e => {
    const { name, value } = e.target
    setAddFormData(prev => ({ ...prev, [name]: value }))
  }

  // Add new rate (single)
  const addRate = async e => {
    e.preventDefault()
    if (
      !addFormData.rate_type ||
      !addFormData.weight ||
      !addFormData.zone_value ||
      !addFormData.rate
    ) {
      toastr.error("Please fill all fields")
      return
    }
    try {
      const { data } = await axios.post(
        `${apiURl}/carriers/${carrierId}/rates`,
        {
          rate_type: addFormData.rate_type,
          weight: parseFloat(addFormData.weight),
          zone_value: parseInt(addFormData.zone_value),
          rate: parseFloat(addFormData.rate),
        }
      )
      if (data.status === "success") {
        toastr.success("Rate added successfully")
        setAddFormData({
          rate_type: "document",
          weight: "",
          zone_value: "",
          rate: "",
        })
        setShowAddModal(false)
        fetchRates() // Refresh list
      } else {
        toastr.error(data.message || "Failed to add rate")
      }
    } catch (err) {
      console.error("Error adding rate:", err)
      const errorMessage =
        err.response?.data?.message || err.message || "Error adding rate"
      if (errorMessage.includes("already exists")) {
        toastr.info(errorMessage)
      } else {
        toastr.error(errorMessage)
      }
    }
  }

  // Remove rate
  const removeRate = async rateId => {
    try {
      const { data } = await axios.delete(
        `${apiURl}/carriers/${carrierId}/rates/${rateId}`
      )
      if (data.status === "success") {
        toastr.success("Rate removed successfully")
        fetchRates() // Refresh list
      } else {
        toastr.error(data.message || "Failed to remove rate")
      }
    } catch (err) {
      console.error("Error removing rate:", err)
      toastr.error("Error removing rate")
    }
  }

  // Open Edit
  const openEdit = rate => {
    setEditFormData({
      rate_type: rate.rate_type,
      weight: rate.weight.toString(),
      zone_value: rate.zone_value.toString(),
      rate: rate.rate.toString(),
    })
    setEditingRateId(rate.id)
    setShowEditModal(true)
  }

  // Handle edit input changes
  const handleEditInputChange = e => {
    const { name, value } = e.target
    setEditFormData(prev => ({ ...prev, [name]: value }))
  }

  // Edit rate
  const editRate = async e => {
    e.preventDefault()
    if (
      !editFormData.rate_type ||
      !editFormData.weight ||
      !editFormData.zone_value ||
      !editFormData.rate
    ) {
      toastr.error("Please fill all fields")
      return
    }
    try {
      const { data } = await axios.put(
        `${apiURl}/carriers/${carrierId}/rates/${editingRateId}`,
        {
          rate_type: editFormData.rate_type,
          weight: parseFloat(editFormData.weight),
          zone_value: parseInt(editFormData.zone_value),
          rate: parseFloat(editFormData.rate),
        }
      )
      if (data.status === "success") {
        toastr.success("Rate updated successfully")
        setEditFormData({
          rate_type: "document",
          weight: "",
          zone_value: "",
          rate: "",
        })
        setShowEditModal(false)
        fetchRates() // Refresh list
      } else {
        toastr.error(data.message || "Failed to update rate")
      }
    } catch (err) {
      console.error("Error updating rate:", err)
      const errorMessage =
        err.response?.data?.message || err.message || "Error updating rate"
      if (errorMessage.includes("already exists")) {
        toastr.info(errorMessage)
      } else {
        toastr.error(errorMessage)
      }
    }
  }

  // Handle Excel file upload for rates (adapted for sheet structure)
  const handleFileUpload = e => {
    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = event => {
      const data = new Uint8Array(event.target.result)
      const workbook = XLSX.read(data, { type: "array" })

      let jsonData = null
      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName]
        const tempJson = XLSX.utils.sheet_to_json(sheet, { header: 1 })
        if (tempJson.length > 0) {
          const header = tempJson[0].map(
            h => h?.toString().trim().toLowerCase() || ""
          )
          const expected = ["rate type", "weight (kg)", "zone value", "rate"]
          if (
            header.length >= 4 &&
            header.slice(0, 4).every((h, i) => h === expected[i])
          ) {
            jsonData = tempJson
            break
          }
        }
      }

      if (!jsonData) {
        toastr.error(
          "The format is not matched. Expected headers: 'Rate Type', 'Weight (Kg)', 'Zone Value', 'Rate'"
        )
        return
      }

      const parsedRates = []
      const errors = []
      const duplicatesList = []
      let rowIndex = 1 // Start after header

      for (const row of jsonData.slice(1)) {
        rowIndex++
        if (
          row.length < 4 ||
          !row[0] ||
          isNaN(parseFloat(row[1])) ||
          isNaN(parseInt(row[2])) ||
          isNaN(parseFloat(row[3]))
        ) {
          errors.push({
            row: rowIndex,
            message: "Invalid or incomplete data row",
          })
          continue
        }

        const rate_type = row[0].toString().trim().toLowerCase()
        if (!["document", "non_document"].includes(rate_type)) {
          errors.push({
            row: rowIndex,
            message: `Invalid rate type: ${row[0]}`,
          })
          continue
        }

        const weight = parseFloat(row[1])
        const zone_value = parseInt(row[2])
        const rate = parseFloat(row[3])

        if (weight < 0 || zone_value < 1 || zone_value > 11 || rate < 0) {
          errors.push({
            row: rowIndex,
            message: `Invalid values: Weight=${weight}, Zone=${zone_value}, Rate=${rate}`,
          })
          continue
        }

        // Check for duplicate
        const existing = rates.find(
          r =>
            r.rate_type === rate_type &&
            Math.abs(r.weight - weight) < 0.0001 &&
            r.zone_value === zone_value
        )

        if (existing) {
          const dupMsg = `Duplicate rate for ${rate_type} ${weight}kg Zone ${zone_value}`
          errors.push({ row: rowIndex, message: dupMsg })
          duplicatesList.push({ rate_type, weight, zone_value, rate })
        } else {
          parsedRates.push({
            rate_type,
            weight: Math.round(weight * 10) / 10, // Round to 1 decimal if needed
            zone_value,
            rate,
          })
        }
      }

      const nonDuplicateErrors = errors.filter(
        err => !err.message.includes("Duplicate")
      )
      setUploadData(parsedRates)
      setDuplicates(duplicatesList)
      setUploadErrors(nonDuplicateErrors)

      if (
        parsedRates.length > 0 ||
        duplicatesList.length > 0 ||
        nonDuplicateErrors.length > 0
      ) {
        setShowUploadModal(true)
      } else {
        toastr.error("No valid data found in the file")
      }
      e.target.value = ""
    }
    reader.readAsArrayBuffer(file)
  }

  // Confirm and upload bulk
  const [uploadLoading, setuploadLoading] = useState(false)
  const confirmUpload = async () => {
    setuploadLoading(true)
    if (uploadData.length === 0) {
      toastr.error("No data to upload")
      return
    }
    try {
      const { data } = await axios.post(
        `${apiURl}/carriers/${carrierId}/rates/bulk`,
        { rates: uploadData }
      )
      if (data.status === "success") {
        setuploadLoading(false)
        toastr.success(
          `Successfully uploaded ${
            data.insertedCount || uploadData.length
          } rates`
        )
        setShowUploadModal(false)
        setUploadData([])
        setUploadErrors([])
        setDuplicates([])
        fetchRates()
      } else {
        toastr.error(data.message || "Failed to upload rates")
      }
    } catch (err) {
      console.error("Error uploading rates:", err)
      toastr.error("Error uploading rates")
    } finally {
      setuploadLoading(false)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <h5 className="card-title mb-0">
              {carrierName ? (
                <>
                  Rates For
                  <span className="text-primary">
                    <Badge
                      pill
                      className="badge-soft-primary ms-1 font-size-16"
                    >
                      {carrierName}
                    </Badge>
                  </span>{" "}
                  Carrier
                </>
              ) : (
                "Rates"
              )}
            </h5>
          </Col>
          <Col className="text-end">
            <div className="d-flex gap-2 align-items-center float-end">
              <Button
                color="success"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="bx bx-upload"></i> Upload Excel Rates
              </Button>
              <Button color="info" size="sm" onClick={downloadSampleExcel}>
                <i className="bx bx-download"></i> Download Sample (.xlsx)
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={() => setShowAddModal(true)}
              >
                <i className="bx bx-plus"></i> Add Single Rate
              </Button>

              <Link
                to="/int-carriers"
                className="btn btn-outline-secondary btn-sm"
              >
                ← Back
              </Link>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </Col>
        </Row>

        {/* Existing Rates Table */}
        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="float-end mt-3">
                  <img
                    src={excelIcon}
                    alt="excel.svg"
                    onClick={handleDownload}
                    disabled={rates.length === 0}
                    style={{
                      cursor: "pointer",
                      border: "1px solid #bfc1c5",
                      borderRadius: "5px",
                      padding: "5px",
                    }}
                    id="excelIcon"
                  />
                  <UncontrolledTooltip placement="left" target="excelIcon">
                    Download Data
                  </UncontrolledTooltip>
                </div>
                {loading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4">
                    <Spinner
                      color="primary"
                      style={{ width: "3rem", height: "3rem" }}
                      className="mb-2"
                    />

                    <small className="text-muted">
                      Please wait—this may take a moment for large files.
                    </small>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={rates}
                    customPageSize={10}
                    isGlobalFilter={true}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Single Rate Modal */}
      <Modal
        isOpen={showAddModal}
        toggle={() => {
          setShowAddModal(false)
          setAddFormData({
            rate_type: "document",
            weight: "",
            zone_value: "",
            rate: "",
          })
        }}
        centered
      >
        <ModalHeader toggle={() => setShowAddModal(false)}>
          Add New Rate
        </ModalHeader>
        <Form onSubmit={addRate}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Rate Type *</Label>
                  <Input
                    type="select"
                    name="rate_type"
                    value={addFormData.rate_type}
                    onChange={handleAddInputChange}
                    required
                  >
                    <option value="document">Document</option>
                    <option value="non_document">Non-Document</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Weight (Kg) *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    name="weight"
                    value={addFormData.weight}
                    onChange={handleAddInputChange}
                    placeholder="e.g., 0.5"
                    min="0"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Zone Value *</Label>
                  <Input
                    type="select"
                    name="zone_value"
                    value={addFormData.zone_value}
                    onChange={handleAddInputChange}
                    required
                  >
                    <option value="">Select Zone</option>
                    {uniqueZoneValues.map(val => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Rate *</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    name="rate"
                    value={addFormData.rate}
                    onChange={handleAddInputChange}
                    placeholder="e.g., 7588.8823"
                    min="0"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              className="btn btn-sm"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button color="primary" className="btn btn-sm" type="submit">
              Add Rate
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Upload Preview Modal */}
      <Modal
        isOpen={showUploadModal}
        toggle={() => {
          setShowUploadModal(false)
          setUploadData([])
          setUploadErrors([])
          setDuplicates([])
          if (uploadLoading) setUploadLoading(false) // Reset on cancel
        }}
        size="lg"
        centered
        aria-busy={uploadLoading} // Accessibility: Indicates loading state
      >
        <ModalHeader toggle={() => setShowUploadModal(false)}>
          Upload Preview
        </ModalHeader>
        <ModalBody>
          {/* Hide preview during loading for cleaner UX */}
          {!uploadLoading ? (
            <>
              {uploadErrors.length > 0 && (
                <Alert color="warning">
                  <strong>{uploadErrors.length} other error(s) found:</strong>
                  <ul>
                    {uploadErrors.map((err, idx) => (
                      <li key={idx}>
                        {err.message} (Row {err.row})
                      </li>
                    ))}
                  </ul>
                  <p>These rows will be skipped during upload.</p>
                </Alert>
              )}
              {duplicates.length > 0 && (
                <div>
                  <Alert color="info">
                    <strong>
                      {duplicates.length} duplicate rate(s) found and will be
                      skipped:
                    </strong>
                  </Alert>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Rate Type</th>
                        <th>Weight (Kg)</th>
                        <th>Zone Value</th>
                        <th>Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicates.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.rate_type}</td>
                          <td>{row.weight}</td>
                          <td>{row.zone_value}</td>
                          <td>{parseFloat(row.rate).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              {uploadData.length > 0 && (
                <p className="text-success">
                  {uploadData.length} new rates will be uploaded.
                </p>
              )}
              {uploadData.length === 0 &&
                duplicates.length === 0 &&
                uploadErrors.length === 0 && <p>No valid data to upload.</p>}
            </>
          ) : (
            // Centered loading during upload
            <div className="d-flex flex-column align-items-center justify-content-center py-4">
              <Spinner
                color="primary"
                style={{ width: "3rem", height: "3rem" }}
                className="mb-2"
              />
              <p className="text-muted mb-0">
                Uploading {uploadData.length} rates...
              </p>
              <small className="text-muted">
                Please wait—this may take a moment for large files.
              </small>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            className="btn btn-sm"
            onClick={() => setShowUploadModal(false)}
            disabled={uploadLoading} // Disable cancel during upload (optional: allow it for user control)
          >
            Cancel
          </Button>
          <Button
            color="success"
            className="btn btn-sm"
            onClick={confirmUpload}
            disabled={uploadData.length === 0 || uploadLoading} // Disable if no data or loading
            aria-label={
              uploadLoading
                ? "Uploading rates..."
                : `Upload ${uploadData.length} Rates`
            }
          >
            {uploadLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                Upload {uploadData.length} Rates
                {duplicates.length > 0 &&
                  ` (skipping ${duplicates.length} duplicates)`}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        toggle={() => {
          setShowEditModal(false)
          setEditFormData({
            rate_type: "document",
            weight: "",
            zone_value: "",
            rate: "",
          })
        }}
        centered
      >
        <ModalHeader toggle={() => setShowEditModal(false)}>
          Edit Rate
        </ModalHeader>
        <Form onSubmit={editRate}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Rate Type *</Label>
                  <Input
                    type="select"
                    name="rate_type"
                    value={editFormData.rate_type}
                    onChange={handleEditInputChange}
                    required
                  >
                    <option value="document">Document</option>
                    <option value="non_document">Non-Document</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Weight (Kg) *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    name="weight"
                    value={editFormData.weight}
                    onChange={handleEditInputChange}
                    placeholder="e.g., 0.5"
                    min="0"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Zone Value *</Label>
                  <Input
                    type="select"
                    name="zone_value"
                    value={editFormData.zone_value}
                    onChange={handleEditInputChange}
                    required
                  >
                    <option value="">Select Zone</option>
                    {uniqueZoneValues.map(val => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Rate *</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    name="rate"
                    value={editFormData.rate}
                    onChange={handleEditInputChange}
                    placeholder="e.g., 7588.8823"
                    min="0"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              className="btn btn-sm"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
            <Button color="primary" className="btn btn-sm" type="submit">
              Update Rate
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(!showDeleteModal)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(!showDeleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this Rate? This action cannot be
          undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            className="btn btn-sm"
            onClick={() => setShowDeleteModal(!showDeleteModal)}
          >
            Cancel
          </Button>
          <Button color="danger" className="btn btn-sm" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default RatesForCarrier
